import { FC } from 'react';
import { Card, Descriptions, InputNumber, Select, Space, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { NAMESPACE } from '@/constants';
import type { IoTemplate, IoTemplateColumn, IoPricingSettings } from '@/api';

const { Text } = Typography;

interface Props {
  template?: IoTemplate;
  /** Working copy of the columns, so a change here re-renders the preview. */
  columns: IoTemplateColumn[];
  onColumnsChange: (columns: IoTemplateColumn[]) => void;
  /** Working copy of the money rules, overridable for this document only. */
  pricing?: IoPricingSettings;
  onPricingChange: (pricing: IoPricingSettings) => void;
}

const rate = (value?: { value: number; type: 'percent' | 'absolute' }): string => {
  if (!value?.value) return '—';
  return value.type === 'percent' ? `${value.value}%` : String(value.value);
};

/**
 * What the selected template will put on the document, and a way to change it
 * for this IO.
 *
 * Raising is the moment these settings matter, so a column can be shown or
 * hidden here without leaving the screen. The change applies to this document
 * only — the template itself is edited under Media Plans, IO Templates.
 */
export const TemplateSummary: FC<Props> = ({
  template,
  columns,
  onColumnsChange,
  pricing,
  onPricingChange
}) => {
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);

  if (!template || !columns.length) return null;

  const toggle = (field: string) =>
    onColumnsChange(
      columns.map((column) =>
        column.field === field ? { ...column, visible: !column.visible } : column
      )
    );

  const visibleCount = columns.filter((column) => column.visible).length;

  return (
    <Card
      size="small"
      title={t('templateSummaryTitle')}
      style={{ marginTop: 20 }}
      styles={{ body: { padding: '16px 18px 18px' } }}
    >
      <Descriptions size="small" column={1} colon={false}>
        <Descriptions.Item label={t('pricingCurrency')}>
          {template.pricing?.currency ?? 'GBP'}
        </Descriptions.Item>
        <Descriptions.Item label={t('pricingDiscount')}>
          {pricing ? (
            <Space.Compact>
              <InputNumber
                min={0}
                step={0.5}
                size="small"
                value={pricing.discount?.value ?? 0}
                aria-label={t('pricingDiscount')}
                onChange={(value) =>
                  onPricingChange({
                    ...pricing,
                    discount: { ...pricing.discount, value: Number(value) || 0 }
                  })
                }
                style={{ width: 90 }}
              />
              <Select
                size="small"
                value={pricing.discount?.type ?? 'percent'}
                onChange={(type) =>
                  onPricingChange({ ...pricing, discount: { ...pricing.discount, type } })
                }
                getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
                style={{ width: 90 }}
                options={[
                  { label: '%', value: 'percent' },
                  { label: t('pricingAbsolute'), value: 'absolute' }
                ]}
              />
            </Space.Compact>
          ) : (
            rate(template.pricing?.discount)
          )}
        </Descriptions.Item>
        <Descriptions.Item label={template.pricing?.taxFirst?.label || t('pricingTaxFirst')}>
          {rate(template.pricing?.taxFirst)}
        </Descriptions.Item>
      </Descriptions>

      <Text type="secondary" style={{ fontSize: 12 }}>
        {t('templateSummaryColumns', { visible: visibleCount, total: columns.length })}
      </Text>
      <div style={{ marginTop: 10 }}>
        <Space size={[4, 6]} wrap>
          {columns.map((column) => (
            <Tag.CheckableTag
              key={column.field}
              checked={column.visible}
              onChange={() => toggle(column.field)}
              // At least one column must stay on or the document has no table.
              style={
                column.visible && visibleCount === 1 ? { pointerEvents: 'none', opacity: 0.6 } : undefined
              }
            >
              {column.label}
            </Tag.CheckableTag>
          ))}
        </Space>
      </div>

      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 14 }}>
        {t('templateSummaryHint')}
      </Text>
    </Card>
  );
};

export default TemplateSummary;
